import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import { useGlobalSearchParams } from "expo-router";
import { buildAcquisitionUrl } from "./acquisitionParams";

const AcquisitionContext = createContext();

const getSearchParamValue = (value) => {
  if (Array.isArray(value)) {
    return value[0] || null;
  }

  return value || null;
};

/**
 * Hook to access acquisition tracking context
 * @returns {{
 *  acquisitionRef: string | null,
 *  fbclid: string | null,
 *  utmSource: string | null,
 *  utmMedium: string | null,
 *  utmCampaign: string | null,
 *  utmContent: string | null,
 *  utmAdset: string | null,
 *  utmTerm: string | null,
 *  trialOffer: string | null,
 *  buildUrlWithParams: (path: string) => string,
 *  clearAcquisitionData: () => void
 * }}
 */
export const useAcquisition = () => {
  const context = useContext(AcquisitionContext);
  if (!context) {
    throw new Error("useAcquisition must be used within AcquisitionProvider");
  }
  return context;
};

/**
 * AcquisitionProvider
 * Captures and maintains the 'ref', 'fbclid', and UTM URL parameters throughout the user session
 *
 * This enables tracking of marketing campaign sources (e.g., Instagram ads, Facebook ads)
 * without using localStorage or cookies.
 *
 * Convention for ref values:
 * - Instagram ads: ref=ig_ad_launch_video1, ref=ig_ad_es_coaches_carousel3
 * - Instagram profile: ref=ig_profile_bio_main, ref=ig_profile_linktree_trial
 *
 * The fbclid parameter is automatically added by Facebook to track ad clicks.
 *
 * All parameters are:
 * - Captured once when user first enters the app
 * - Maintained in memory during the session
 * - Propagated through all navigation within the funnel
 * - Saved to Firestore when coach completes onboarding
 * - Lost if user closes/reloads without the parameters
 */
export const AcquisitionProvider = ({ children }) => {
  const params = useGlobalSearchParams() || {};
  const [acquisitionRef, setAcquisitionRef] = useState(() =>
    getSearchParamValue(params.ref)
  );
  const [fbclid, setFbclid] = useState(() => getSearchParamValue(params.fbclid));
  const [utmSource, setUtmSource] = useState(() =>
    getSearchParamValue(params.utm_source)
  );
  const [utmMedium, setUtmMedium] = useState(() =>
    getSearchParamValue(params.utm_medium)
  );
  const [utmCampaign, setUtmCampaign] = useState(() =>
    getSearchParamValue(params.utm_campaign)
  );
  const [utmContent, setUtmContent] = useState(() =>
    getSearchParamValue(params.utm_content)
  );
  const [utmAdset, setUtmAdset] = useState(() =>
    getSearchParamValue(params.utm_adset)
  );
  const [utmTerm, setUtmTerm] = useState(() =>
    getSearchParamValue(params.utm_term)
  );
  const [trialOffer, setTrialOffer] = useState(() =>
    getSearchParamValue(params.trial_offer)
  );

  useEffect(() => {
    // Capture the 'ref' parameter from URL if present
    // Note: Other URL parameters (like 'lang') are handled separately by their own hooks
    // and will not interfere with acquisition tracking
    const refParam = getSearchParamValue(params.ref);

    if (refParam && !acquisitionRef) {
      console.log("[AcquisitionContext] Captured ref parameter:", refParam);
      setAcquisitionRef(refParam);
    }
  }, [params.ref, acquisitionRef]);

  useEffect(() => {
    // Capture the 'fbclid' parameter from URL if present
    // This is Facebook's click identifier automatically added to URLs from Facebook ads
    const fbclidParam = getSearchParamValue(params.fbclid);

    if (fbclidParam && !fbclid) {
      console.log(
        "[AcquisitionContext] Captured fbclid parameter:",
        fbclidParam
      );
      setFbclid(fbclidParam);
    }
  }, [params.fbclid, fbclid]);

  useEffect(() => {
    // Capture UTM parameters from URL if present
    const utmSourceParam = getSearchParamValue(params.utm_source);
    const utmMediumParam = getSearchParamValue(params.utm_medium);
    const utmCampaignParam = getSearchParamValue(params.utm_campaign);
    const utmContentParam = getSearchParamValue(params.utm_content);
    const utmAdsetParam = getSearchParamValue(params.utm_adset);
    const utmTermParam = getSearchParamValue(params.utm_term);

    if (utmSourceParam && !utmSource) {
      console.log(
        "[AcquisitionContext] Captured utm_source parameter:",
        utmSourceParam
      );
      setUtmSource(utmSourceParam);
    }

    if (utmMediumParam && !utmMedium) {
      console.log(
        "[AcquisitionContext] Captured utm_medium parameter:",
        utmMediumParam
      );
      setUtmMedium(utmMediumParam);
    }

    if (utmCampaignParam && !utmCampaign) {
      console.log(
        "[AcquisitionContext] Captured utm_campaign parameter:",
        utmCampaignParam
      );
      setUtmCampaign(utmCampaignParam);
    }

    if (utmContentParam && !utmContent) {
      console.log(
        "[AcquisitionContext] Captured utm_content parameter:",
        utmContentParam
      );
      setUtmContent(utmContentParam);
    }

    if (utmAdsetParam && !utmAdset) {
      console.log(
        "[AcquisitionContext] Captured utm_adset parameter:",
        utmAdsetParam
      );
      setUtmAdset(utmAdsetParam);
    }

    if (utmTermParam && !utmTerm) {
      console.log(
        "[AcquisitionContext] Captured utm_term parameter:",
        utmTermParam
      );
      setUtmTerm(utmTermParam);
    }
  }, [
    params.utm_source,
    params.utm_medium,
    params.utm_campaign,
    params.utm_content,
    params.utm_adset,
    params.utm_term,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
  ]);

  useEffect(() => {
    const trialOfferParam = getSearchParamValue(params.trial_offer);

    if (trialOfferParam && !trialOffer) {
      console.log(
        "[AcquisitionContext] Captured trial_offer parameter:",
        trialOfferParam
      );
      setTrialOffer(trialOfferParam);
    }
  }, [params.trial_offer, trialOffer]);

  /**
   * Helper function to build URLs with acquisition parameters
   * @param {string} path - The path to navigate to (e.g., '/signup', '/login')
   * @returns {string} The path with ref and/or fbclid parameters appended if they exist
   */
  const buildUrlWithParams = useCallback((path) => {
    return buildAcquisitionUrl(path, {
      acquisitionRef,
      fbclid,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmAdset,
      utmTerm,
      trialOffer,
    });
  }, [
    acquisitionRef,
    fbclid,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmAdset,
    utmTerm,
    trialOffer,
  ]);

  /**
   * Clear the acquisition data (e.g., on logout)
   */
  const clearAcquisitionData = useCallback(() => {
    console.log("[AcquisitionContext] Clearing acquisition data");
    setAcquisitionRef(null);
    setFbclid(null);
    setUtmSource(null);
    setUtmMedium(null);
    setUtmCampaign(null);
    setUtmContent(null);
    setUtmAdset(null);
    setUtmTerm(null);
    setTrialOffer(null);
  }, []);

  const value = useMemo(
    () => ({
      acquisitionRef,
      fbclid,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmAdset,
      utmTerm,
      trialOffer,
      buildUrlWithParams,
      clearAcquisitionData,
    }),
    [
      acquisitionRef,
      fbclid,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmAdset,
      utmTerm,
      trialOffer,
      buildUrlWithParams,
      clearAcquisitionData,
    ]
  );

  return (
    <AcquisitionContext.Provider value={value}>
      {children}
    </AcquisitionContext.Provider>
  );
};
